/** @jsx jsx */
import { jsx } from '@emotion/core'
import { Button, ButtonGroup, Cell, Grid, HFlow, isEmpty, VFlow } from 'bold-ui'
import { Box } from 'components/Box'
import { CheckboxField, usePecField } from 'components/form'
import { IdentidadeGeneroEnum, SexoEnum } from 'graphql/types.generated'
import { useEffect, useState } from 'react'
import { MetaRoot } from 'util/metaPath'

import { AcompanhamentoFilterModel } from '../model/model-acompanhamentoFilter'

enum SexoIdentidadeGeneroButtonGroupOption {
  SEXO = 'SEXO',
  IDENTIDADE_GENERO = 'IDENTIDADE_GENERO',
}

interface SexoIdentidadeGeneroButtonGroup<T> {
  onChange?: (name: string, value?: any) => void
  meta: MetaRoot<T>
}

export function SexoIdentidadeGeneroButtonGroup<T extends AcompanhamentoFilterModel>(
  props: SexoIdentidadeGeneroButtonGroup<T>
) {
  const { onChange, meta } = props

  const [option, setOption] = useState<SexoIdentidadeGeneroButtonGroupOption>(
    SexoIdentidadeGeneroButtonGroupOption.SEXO
  )

  const {
    input: { value: sexoValue },
  } = usePecField({ name: meta.sexo, subscription: { value: true } })
  const {
    input: { value: identidadeGeneroValue },
  } = usePecField({ name: meta.identidadeGenero, subscription: { value: true } })

  const handleChangeButtonGroup = (selectedOption: SexoIdentidadeGeneroButtonGroupOption) => {
    setOption(selectedOption)
    if (selectedOption === SexoIdentidadeGeneroButtonGroupOption.SEXO) {
      onChange(meta.identidadeGenero.absolutePath(), undefined)
    }
    if (selectedOption === SexoIdentidadeGeneroButtonGroupOption.IDENTIDADE_GENERO) {
      onChange(meta.sexo.absolutePath(), undefined)
    }
  }

  useEffect(() => {
    if (isEmpty(sexoValue) && !isEmpty(identidadeGeneroValue)) {
      setOption(SexoIdentidadeGeneroButtonGroupOption.IDENTIDADE_GENERO)
    }

    if (!isEmpty(sexoValue) && isEmpty(identidadeGeneroValue)) {
      setOption(SexoIdentidadeGeneroButtonGroupOption.SEXO)
    }
  }, [identidadeGeneroValue, sexoValue])

  return (
    <VFlow vSpacing={0.5}>
      <ButtonGroup>
        <Button
          kind={option === SexoIdentidadeGeneroButtonGroupOption.SEXO ? 'primary' : 'normal'}
          size='small'
          skin='default'
          onClick={() => handleChangeButtonGroup(SexoIdentidadeGeneroButtonGroupOption.SEXO)}
        >
          Sexo
        </Button>
        <Button
          kind={option === SexoIdentidadeGeneroButtonGroupOption.IDENTIDADE_GENERO ? 'primary' : 'normal'}
          size='small'
          skin='default'
          onClick={() => handleChangeButtonGroup(SexoIdentidadeGeneroButtonGroupOption.IDENTIDADE_GENERO)}
        >
          Identidade de gênero
        </Button>
      </ButtonGroup>
      <Box>
        {option === SexoIdentidadeGeneroButtonGroupOption.SEXO ? (
          <HFlow hSpacing={1}>
            <CheckboxField name={meta.sexo} value={SexoEnum.FEMININO} label='Feminino' />
            <CheckboxField name={meta.sexo} value={SexoEnum.MASCULINO} label='Masculino' />
            <CheckboxField name={meta.sexo} value={SexoEnum.INDETERMINADO} label='Indeterminado' />
          </HFlow>
        ) : (
          <Grid gap={1}>
            <Cell size={4}>
              <CheckboxField
                name={meta.identidadeGenero}
                label='Mulher cisgênero'
                value={IdentidadeGeneroEnum.MULHER_CIS}
              />
            </Cell>
            <Cell size={4}>
              <CheckboxField
                name={meta.identidadeGenero}
                label='Homem cisgênero'
                value={IdentidadeGeneroEnum.HOMEM_CIS}
              />
            </Cell>
            <Cell size={4}>
              <CheckboxField
                name={meta.identidadeGenero}
                label='Não-Binário'
                value={IdentidadeGeneroEnum.NAO_BINARIO}
              />
            </Cell>
            <Cell size={4}>
              <CheckboxField
                name={meta.identidadeGenero}
                label='Mulher transgênero'
                value={IdentidadeGeneroEnum.MULHER_TRANS}
              />
            </Cell>
            <Cell size={4}>
              <CheckboxField
                name={meta.identidadeGenero}
                label='Homem transgênero'
                value={IdentidadeGeneroEnum.HOMEM_TRANS}
              />
            </Cell>
            <Cell size={4}>
              <CheckboxField
                name={meta.identidadeGenero}
                label='Transgênero'
                value={IdentidadeGeneroEnum.TRANSGENERO}
              />
            </Cell>
            <Cell size={4}>
              <CheckboxField name={meta.identidadeGenero} label='Travesti' value={IdentidadeGeneroEnum.TRAVESTI} />
            </Cell>
            <Cell size={4}>
              <CheckboxField
                name={meta.identidadeGenero}
                label='Outro'
                value={IdentidadeGeneroEnum.OUTRO_IDENTIDADE_GENERO}
              />
            </Cell>
            <Cell size={4} />
          </Grid>
        )}
      </Box>
    </VFlow>
  )
}
